import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../shared/ui/Modal/Modal';
import { FormField } from '../../shared/ui/FormField/FormField';
import { FormTextarea } from '../../shared/ui/FormField/FormTextarea';
import { useToast } from '../../shared/ui/feedback/ToastContext';
import { createAuthHeaders } from '../../shared/api/auth';
import { API_URL } from '../../config';

interface CreateOrgModalProps {
  open: boolean;
  onClose: () => void;
}

function toSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function CreateOrgModal({ open, onClose }: CreateOrgModalProps) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({});

  useEffect(() => {
    if (!open) {
      setName('');
      setSlug('');
      setDescription('');
      setSlugEdited(false);
      setErrors({});
    }
  }, [open]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    if (!slugEdited) setSlug(toSlug(val));
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugEdited(true);
    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const validate = () => {
    const errs: { name?: string; slug?: string } = {};
    if (!name.trim()) errs.name = 'Company name is required';
    if (!slug.trim()) errs.slug = 'Slug is required';
    else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug) && slug.length > 1) {
      errs.slug = 'Slug must contain only lowercase letters, numbers, and hyphens';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...createAuthHeaders() },
        body: JSON.stringify({ name: name.trim(), slug, description: description.trim() || undefined }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Company added');
        onClose();
        // A new company is a label to put people under; there is nothing to switch into.
        window.location.reload();
      } else {
        toast.error(data.message || data.error || 'Failed to add the company');
      }
    } catch {
      toast.error('Connection error -- please try again');
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = name.trim().length > 0 && slug.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Add company" onClose={onClose} />
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FormField
            label="Company name"
            required
            placeholder="e.g. Acme Corp"
            value={name}
            onChange={handleNameChange}
            error={errors.name}
          />
          <FormField
            label="Slug"
            required
            placeholder="e.g. acme-corp"
            value={slug}
            onChange={handleSlugChange}
            error={errors.slug}
            hint="Used in URLs -- lowercase letters and hyphens only"
          />
          <FormTextarea
            label="Description"
            placeholder="e.g. Our central workspace for learning and events"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
      </ModalBody>
      <ModalFooter
        onCancel={onClose}
        onConfirm={handleSubmit}
        confirmText="Add company"
        confirmDisabled={!canSubmit}
        confirmLoading={saving}
      />
    </Modal>
  );
}
